import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { updatePageSEO, SEO_CONFIGS } from '../../utils/seo';

export default function CareersPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Update SEO
    updatePageSEO(SEO_CONFIGS.careers);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/80 backdrop-blur-md'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="text-2xl font-bold text-[#2D7A4F]">
              おかやま学習塾
            </Link>
            
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/services" className="text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium whitespace-nowrap">事業内容</Link>
              <Link to="/about" className="text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium whitespace-nowrap">会社概要</Link>
              <Link to="/careers" className="text-[#2D7A4F] hover:text-[#236038] transition-colors text-base font-medium whitespace-nowrap border-b-2 border-[#2D7A4F]">求人情報</Link>
              <Link to="/blog" className="text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium whitespace-nowrap">ブログ・新着情報</Link>
              <Link to="/trial" className="text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium whitespace-nowrap">体験申し込み</Link>
              <a href="https://line.me/R/ti/p/@okayamajuku" target="_blank" rel="noopener noreferrer" className="bg-[#2D7A4F] text-white px-6 py-2.5 rounded-full hover:bg-[#236038] transition-colors flex items-center space-x-2 text-base font-medium whitespace-nowrap">
                <i className="ri-line-fill text-lg"></i>
                <span>LINE相談</span>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-[#2D7A4F] w-10 h-10 flex items-center justify-center"
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <div className="px-6 py-4 space-y-4">
              <Link to="/services" className="block text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>事業内容</Link>
              <Link to="/about" className="block text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>会社概要</Link>
              <Link to="/careers" className="block text-[#2D7A4F] hover:text-[#236038] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>求人情報</Link>
              <Link to="/blog" className="block text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>ブログ・新着情報</Link>
              <Link to="/trial" className="block text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>体験申し込み</Link>
              <a href="https://line.me/R/ti/p/@okayamajuku" target="_blank" rel="noopener noreferrer" className="block bg-[#2D7A4F] text-white px-6 py-3 rounded-full hover:bg-[#236038] transition-colors text-center text-base font-medium">
                <i className="ri-line-fill mr-2"></i>LINE相談
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative h-[40vh] min-h-[350px] sm:min-h-[400px] flex items-center justify-center pt-20">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/f37b31c86a11368c601497534c425f9a.jpg" 
            alt="おかやま学習塾の講師陣"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 text-center px-6">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6">求人情報</h1>
          <div className="flex items-center justify-center space-x-3 text-white/90 text-sm">
            <Link to="/" className="hover:text-white transition-colors">ホーム</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>求人情報</span>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 sm:py-20 lg:py-32 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
              一緒に子どもたちの<br className="sm:hidden" />未来を創りませんか
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto px-4">
              岡山市北区で地域に根ざした教育を提供する私たちと一緒に、子どもたちの夢を叶えるお手伝いをしてくださる講師を募集しています。
            </p>
          </div>

          {/* Benefits */}
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            <div className="bg-[#F1F8F4] rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-[#2D7A4F] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">柔軟な勤務時間</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                週2日から勤務可能。学生の方や副業の方も歓迎します
              </p>
            </div>

            <div className="bg-[#F1F8F4] rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-[#2D7A4F] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-graduation-cap-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">充実の研修制度</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                未経験でも安心。先輩講師が丁寧に指導方法を教えます
              </p>
            </div>

            <div className="bg-[#F1F8F4] rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-[#2D7A4F] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-heart-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">やりがいのある仕事</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                生徒様の成長を間近で見守り、志望校合格の喜びを共有できます
              </p>
            </div>
          </div>

          {/* Job Listings */}
          <div className="space-y-8">
            {/* Position 1 */}
            <div className="bg-white border-2 border-gray-200 rounded-3xl overflow-hidden hover:border-[#2D7A4F] transition-colors">
              <div className="p-8 lg:p-12">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
                  <div>
                    <div className="inline-block bg-[#2D7A4F] text-white px-4 py-2 rounded-full text-sm font-medium mb-4 whitespace-nowrap">
                      正社員
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">個別指導講師（正社員）</h3>
                    <p className="text-gray-600">岡山市北区の小中高生への個別指導</p>
                  </div>
                  <div className="mt-4 lg:mt-0">
                    <div className="text-3xl font-bold text-[#2D7A4F]">月給25万円〜</div>
                    <div className="text-sm text-gray-500 text-right">経験・能力により優遇</div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-briefcase-line text-[#2D7A4F] mr-2"></i>
                      仕事内容
                    </h4>
                    <p className="text-base text-gray-700 leading-relaxed pl-7">
                      岡山市北区の小学生・中学生・高校生を対象とした個別指導。一人ひとりの学力や目標に合わせたカリキュラム作成、授業実施、学習相談、保護者面談などを担当していただきます。
                    </p>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-user-star-line text-[#2D7A4F] mr-2"></i>
                      応募資格
                    </h4>
                    <ul className="space-y-2 pl-7">
                      <li className="text-base text-gray-700 flex items-start">
                        <span className="text-[#2D7A4F] mr-2">•</span>
                        <span>四年制大学卒業以上（教育学部卒業者優遇）</span>
                      </li>
                      <li className="text-base text-gray-700 flex items-start">
                        <span className="text-[#2D7A4F] mr-2">•</span>
                        <span>教育に対する情熱と責任感をお持ちの方</span>
                      </li>
                      <li className="text-base text-gray-700 flex items-start">
                        <span className="text-[#2D7A4F] mr-2">•</span>
                        <span>岡山市北区の学校事情に詳しい方歓迎</span>
                      </li>
                      <li className="text-base text-gray-700 flex items-start">
                        <span className="text-[#2D7A4F] mr-2">•</span>
                        <span>塾講師経験者優遇（未経験者も歓迎）</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-time-line text-[#2D7A4F] mr-2"></i>
                      勤務時間
                    </h4>
                    <p className="text-base text-gray-700 pl-7">14:00〜22:00（休憩1時間）</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-calendar-line text-[#2D7A4F] mr-2"></i>
                      休日・休暇
                    </h4>
                    <p className="text-base text-gray-700 pl-7">週休2日制（日曜・祝日）、夏季休暇、年末年始休暇、有給休暇</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-gift-line text-[#2D7A4F] mr-2"></i>
                      待遇・福利厚生
                    </h4>
                    <ul className="space-y-2 pl-7">
                      <li className="text-base text-gray-700 flex items-start">
                        <span className="text-[#2D7A4F] mr-2">•</span>
                        <span>社会保険完備（健康保険、厚生年金、雇用保険、労災保険）</span>
                      </li>
                      <li className="text-base text-gray-700 flex items-start">
                        <span className="text-[#2D7A4F] mr-2">•</span>
                        <span>交通費全額支給</span>
                      </li>
                      <li className="text-base text-gray-700 flex items-start">
                        <span className="text-[#2D7A4F] mr-2">•</span>
                        <span>昇給年1回、賞与年2回</span>
                      </li>
                      <li className="text-base text-gray-700 flex items-start">
                        <span className="text-[#2D7A4F] mr-2">•</span>
                        <span>研修制度充実</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-gray-200">
                  <a href="https://line.me/R/ti/p/@okayamajuku" target="_blank" rel="noopener noreferrer" className="inline-flex items-center bg-[#2D7A4F] text-white px-8 py-4 rounded-full hover:bg-[#236038] transition-all text-base font-semibold shadow-lg whitespace-nowrap">
                    <i className="ri-line-fill text-xl mr-2"></i>
                    <span>LINEで応募する</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Position 2 */}
            <div className="bg-white border-2 border-gray-200 rounded-3xl overflow-hidden hover:border-[#2D7A4F] transition-colors">
              <div className="p-8 lg:p-12">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
                  <div>
                    <div className="inline-block bg-[#FF8A65] text-white px-4 py-2 rounded-full text-sm font-medium mb-4 whitespace-nowrap">
                      アルバイト・パート
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">個別指導講師（アルバイト）</h3>
                    <p className="text-gray-600">週2日から勤務可能・学生歓迎</p>
                  </div>
                  <div className="mt-4 lg:mt-0">
                    <div className="text-3xl font-bold text-[#2D7A4F]">時給1,500円〜</div>
                    <div className="text-sm text-gray-500 text-right">経験・能力により優遇</div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-briefcase-line text-[#2D7A4F] mr-2"></i>
                      仕事内容
                    </h4>
                    <p className="text-base text-gray-700 leading-relaxed pl-7">
                      岡山市北区の小中高生への個別指導。得意科目を活かして、生徒様の学習をサポートしていただきます。授業準備や報告書作成もお願いします。
                    </p>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-user-star-line text-[#2D7A4F] mr-2"></i>
                      応募資格
                    </h4>
                    <ul className="space-y-2 pl-7">
                      <li className="text-base text-gray-700 flex items-start">
                        <span className="text-[#2D7A4F] mr-2">•</span>
                        <span>大学生・大学院生、または四年制大学卒業以上</span>
                      </li>
                      <li className="text-base text-gray-700 flex items-start">
                        <span className="text-[#2D7A4F] mr-2">•</span>
                        <span>週2日以上、1日2時間以上勤務できる方</span>
                      </li>
                      <li className="text-base text-gray-700 flex items-start">
                        <span className="text-[#2D7A4F] mr-2">•</span>
                        <span>教えることが好きな方、未経験者歓迎</span>
                      </li>
                      <li className="text-base text-gray-700 flex items-start">
                        <span className="text-[#2D7A4F] mr-2">•</span>
                        <span>岡山市北区在住または通勤可能な方</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-time-line text-[#2D7A4F] mr-2"></i>
                      勤務時間
                    </h4>
                    <p className="text-base text-gray-700 pl-7">15:00〜22:00の間で2時間以上（シフト制）</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-calendar-line text-[#2D7A4F] mr-2"></i>
                      勤務日数
                    </h4>
                    <p className="text-base text-gray-700 pl-7">週2日〜（曜日・時間は相談可能）</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-gift-line text-[#2D7A4F] mr-2"></i>
                      待遇・福利厚生
                    </h4>
                    <ul className="space-y-2 pl-7">
                      <li className="text-base text-gray-700 flex items-start">
                        <span className="text-[#2D7A4F] mr-2">•</span>
                        <span>交通費支給（上限月1万円）</span>
                      </li>
                      <li className="text-base text-gray-700 flex items-start">
                        <span className="text-[#2D7A4F] mr-2">•</span>
                        <span>昇給あり（能力・経験により）</span>
                      </li>
                      <li className="text-base text-gray-700 flex items-start">
                        <span className="text-[#2D7A4F] mr-2">•</span>
                        <span>研修制度あり（未経験者も安心）</span>
                      </li>
                      <li className="text-base text-gray-700 flex items-start">
                        <span className="text-[#2D7A4F] mr-2">•</span>
                        <span>テスト期間中の休暇取得可能（学生の方）</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-gray-200">
                  <a href="https://line.me/R/ti/p/@okayamajuku" target="_blank" rel="noopener noreferrer" className="inline-flex items-center bg-[#2D7A4F] text-white px-8 py-4 rounded-full hover:bg-[#236038] transition-all text-base font-semibold shadow-lg whitespace-nowrap">
                    <i className="ri-line-fill text-xl mr-2"></i>
                    <span>LINEで応募する</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Work Environment Section */}
      <section className="py-16 sm:py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">働きやすい環境</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="https://readdy.ai/api/search-image?query=modern%20bright%20Japanese%20classroom%20interior%20with%20comfortable%20teaching%20environment%20clean%20organized%20educational%20space%20simple%20clean%20background&width=800&height=600&seq=work-env001&orientation=landscape" 
                alt="おかやま学習塾の教室環境"
                className="w-full h-64 object-cover object-top"
              />
              <div className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-3">快適な教室環境</h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  明るく清潔な教室で、集中して指導に取り組めます。最新の教材や設備も完備しています。
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/f8187281810b86d05276cdca31e8410f.png" 
                alt="おかやま学習塾のスタッフルーム"
                className="w-full h-64 object-cover object-top"
              />
              <div className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-3">チームワーク重視</h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  先輩講師が丁寧にサポート。定期的なミーティングで情報共有し、チーム一丸となって生徒様を支援します。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Section */}
      <section className="py-16 sm:py-20 lg:py-32 bg-[#FAFAFA]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">応募の流れ</h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-[#2D7A4F] rounded-full flex items-center justify-center mx-auto mb-6 text-white text-3xl font-bold">
                1
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">LINEで応募</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                LINEから簡単に応募できます。お気軽にご連絡ください
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-[#2D7A4F] rounded-full flex items-center justify-center mx-auto mb-6 text-white text-3xl font-bold">
                2
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">書類選考</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                履歴書をご提出いただき、書類選考を行います
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-[#2D7A4F] rounded-full flex items-center justify-center mx-auto mb-6 text-white text-3xl font-bold">
                3
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">面接</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                岡山市北区の教室で面接を実施します
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-[#2D7A4F] rounded-full flex items-center justify-center mx-auto mb-6 text-white text-3xl font-bold">
                4
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">採用・研修</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                採用後、充実した研修で指導方法を学びます
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 lg:py-32 bg-[#A5D6A7]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
            一緒に働きませんか？
          </h2>
          <p className="text-base text-gray-700 mb-8 sm:mb-10 px-4">
            岡山市北区の子どもたちの未来を<br className="sm:hidden" />一緒に支えましょう
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center px-4">
            <Link to="/trial" className="bg-[#2D7A4F] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full hover:bg-[#236038] transition-all text-base lg:text-lg font-semibold flex items-center justify-center space-x-2 shadow-lg whitespace-nowrap">
              <span>応募する</span>
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
            <a href="tel:0860123456" className="bg-white text-[#2D7A4F] border-2 border-[#2D7A4F] px-6 sm:px-8 py-3 sm:py-4 rounded-full hover:bg-gray-50 transition-all text-base lg:text-lg font-semibold flex items-center justify-center space-x-2 whitespace-nowrap">
              <i className="ri-phone-fill text-xl"></i>
              <span>086-012-3456</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1B5E3F] text-white mx-6 lg:mx-8 my-8 rounded-3xl">
        <div className="max-w-7xl mx-auto px-8 lg:px-12 py-16">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <h3 className="text-3xl lg:text-4xl font-light mb-6">お気軽にご相談ください</h3>
              <p className="text-white/80 mb-8 leading-relaxed">
                岡山市北区で志望校合格を目指すなら、おかやま学習塾にお任せください。無料体験授業も随時受付中です。
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:0860123456" className="text-white text-xl font-semibold hover:text-[#A5D6A7] transition-colors whitespace-nowrap flex items-center">
                  <i className="ri-phone-fill text-xl mr-2"></i>
                  <span>086-012-3456</span>
                </a>
                <a href="https://line.me/R/ti/p/@okayamajuku" target="_blank" rel="noopener noreferrer" className="bg-white text-[#1B5E3F] px-6 py-3 rounded-full hover:bg-white/90 transition-colors text-center font-semibold whitespace-nowrap">
                  <i className="ri-line-fill mr-2"></i>LINEで相談
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-6 text-white/70">サイトマップ</h4>
              <ul className="space-y-4">
                <li><Link to="/services" className="text-base hover:text-[#A5D6A7] transition-colors underline">事業内容</Link></li>
                <li><Link to="/about" className="text-base hover:text-[#A5D6A7] transition-colors underline">会社概要</Link></li>
                <li><Link to="/careers" className="text-base hover:text-[#A5D6A7] transition-colors underline">求人情報</Link></li>
                <li><Link to="/blog" className="text-base hover:text-[#A5D6A7] transition-colors underline">ブログ・新着情報</Link></li>
                <li><Link to="/trial" className="text-base hover:text-[#A5D6A7] transition-colors underline">体験申し込み</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-6 text-white/70">おかやま学習塾</h4>
              <p className="text-base text-white/80 mb-2">営業時間：15:00 〜 22:00</p>
              <p className="text-base text-white/80 mb-6">定休日：日曜日・祝日</p>
              <h4 className="text-sm font-semibold mb-4 text-white/70">所在地</h4>
              <p className="text-base text-white/80">〒700-0000</p>
              <p className="text-base text-white/80">岡山市北区○○町1-2-3</p>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-white/70 text-sm">
              © 2025 おかやま学習塾 All Rights Reserved.
            </div>
            <div className="flex gap-6 text-sm">
              <a href="#" className="text-white/70 hover:text-white transition-colors underline">プライバシーポリシー</a>
              <a href="#" className="text-white/70 hover:text-white transition-colors underline">特定商取引法</a>
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors underline">Website Builder</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}